import { FilterOptions, FilterValue } from './ListProps';

export type FilterOperator = 'anyof' | '=' | 'contains' | string;

export type DevExtremeFilterTriple = [string, FilterOperator, FilterValue | FilterValue[]];

export type FilterChip = {
  dataField: string;
  label: string;
  value: FilterValue;
  color?: string;
  operator?: 'anyof' | '=';
  customColors?: {
    backgroundColor: string;
    textColor: string;
  };
};

export type LogicalOperator = 'AND' | 'OR';

// Flatten DevExtreme filter expression into simple triples
export const flattenDevExtremeFilter = (expr: unknown): DevExtremeFilterTriple[] => {
  const result: DevExtremeFilterTriple[] = [];

  const walk = (node: unknown) => {
    if (!node) return;
    if (Array.isArray(node)) {
      if (node.length === 3 && typeof node[0] === 'string') {
        result.push(node as DevExtremeFilterTriple);
      } else {
        node.forEach(walk);
      }
    }
  };

  walk(expr);
  return result;
};

// Map URL filters -> chips based on filterGroups config
export const filtersToChips = (filters: unknown, filterGroups: FilterOptions): FilterChip[] => {
  if (!filters || !Array.isArray(filters) || !filterGroups?.groups?.length) return [];

  const flattenFilter = flattenDevExtremeFilter(filters);
  const chips: FilterChip[] = [];

  flattenFilter.forEach(([field, op, value]) => {
    filterGroups.groups
      .filter((group) => group.dataField === field)
      .forEach((group) => {
        const groupField = group.dataField;
        const values = op === 'anyof' && Array.isArray(value) ? value : [value];

        if (group.type === 'reference') {
          // REFERENCE groups (Location, Asset, etc.)
          const refChips = values
            .filter((v) => v !== undefined && v !== null)
            .map<FilterChip>((v) => ({
              dataField: groupField,
              label: String(v),
              value: v as FilterValue,
              operator: 'anyof',
            }));

          chips.push(...refChips);
        } else {
          // NORMAL groups (assignment, state, priority, etc.)
          const itemChips = group.dataSource.flatMap((opt) => {
            const expectedValue = opt?.value;

            return values
              .filter((value) => value === expectedValue)
              .map<FilterChip>((v) => ({
                dataField: groupField,
                label: opt.label,
                value: v as FilterValue,
                customColors: opt.customColors,
              }));
          });

          chips.push(...itemChips);
        }
      });
  });

  return chips;
};

// AND between groups, ANYOF inside group (for URL / DevExtreme)
export const chipsToFilters = (
  chips: FilterChip[],
): (DevExtremeFilterTriple | LogicalOperator)[] => {
  if (!chips.length) return [];

  const grouped = chips.reduce(
    (acc, chip) => {
      if (!acc[chip.dataField]) acc[chip.dataField] = [];
      acc[chip.dataField].push(chip);
      return acc;
    },
    {} as Record<string, FilterChip[]>,
  );

  const fields = Object.keys(grouped);
  if (!fields.length) return [];

  const final: (DevExtremeFilterTriple | LogicalOperator)[] = [];

  fields.forEach((field, fieldIndex) => {
    const chipsForField = grouped[field];

    const values = Array.from(new Set(chipsForField.map((c) => c.value)));
    const expr: DevExtremeFilterTriple = [field, 'anyof', values];

    final.push(expr);
    if (fieldIndex < fields.length - 1) {
      final.push('AND');
    }
  });

  return final;
};

// NORMALIZER: convert anyof => OR-of-'=' for backend
export const normalizeAnyOfForBackend = (expr: unknown): unknown => {
  if (!expr) return expr;

  if (Array.isArray(expr)) {
    // [field, 'anyof', [v1, v2, ...]]
    if (
      expr.length === 3 &&
      typeof expr[0] === 'string' &&
      expr[1] === 'anyof' &&
      Array.isArray(expr[2])
    ) {
      const [field, , values] = expr as [string, 'anyof', FilterValue[]];
      if (!values.length) return [];

      if (values.length === 1) {
        // single value -> simple '=' triple
        return [field, '=', values[0]] as DevExtremeFilterTriple;
      }

      const orGroup: (DevExtremeFilterTriple | LogicalOperator)[] = [];
      values.forEach((v, i) => {
        orGroup.push([field, '=', v]);
        if (i < values.length - 1) {
          orGroup.push('OR');
        }
      });
      return orGroup;
    }

    // recurse into children
    return expr.map((sub) => normalizeAnyOfForBackend(sub));
  }

  return expr;
};
